import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

export interface OfficeClock {
  city: string;
  country: string;
  timeZone: string;
}

// Every office keeps the same local business hours (Mon-Fri, 9 AM to 6 PM).
const OPEN_HOUR = 9;
const CLOSE_HOUR = 18;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const localParts = (now: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second')),
    weekday: get('weekday'),
  };
};

const ClockFace: React.FC<{ hour: number; minute: number; second: number; open: boolean }> = ({
  hour,
  minute,
  second,
  open,
}) => {
  const hourAngle = ((hour % 12) + minute / 60) * 30;
  const minuteAngle = (minute + second / 60) * 6;
  const secondAngle = second * 6;

  return (
    <svg viewBox="0 0 100 100" className="h-20 w-20" aria-hidden="true">
      <circle cx="50" cy="50" r="46" fill="#fff" stroke={open ? '#0E9ED5' : '#cbd5e1'} strokeWidth="3" />
      {Array.from({ length: 12 }).map((_, i) => (
        <line
          key={i}
          x1="50"
          y1={i % 3 === 0 ? 9 : 11}
          x2="50"
          y2="16"
          stroke={i % 3 === 0 ? '#0f172a' : '#94a3b8'}
          strokeWidth={i % 3 === 0 ? 3 : 1.5}
          strokeLinecap="round"
          transform={`rotate(${i * 30} 50 50)`}
        />
      ))}
      <line x1="50" y1="50" x2="50" y2="28" stroke="#0f172a" strokeWidth="4.5" strokeLinecap="round" transform={`rotate(${hourAngle} 50 50)`} />
      <line x1="50" y1="50" x2="50" y2="18" stroke="#0f172a" strokeWidth="3" strokeLinecap="round" transform={`rotate(${minuteAngle} 50 50)`} />
      <line x1="50" y1="58" x2="50" y2="15" stroke="#0E9ED5" strokeWidth="1.5" strokeLinecap="round" transform={`rotate(${secondAngle} 50 50)`} />
      <circle cx="50" cy="50" r="3.5" fill="#0E9ED5" />
    </svg>
  );
};

const OfficeClocks: React.FC<{ offices: OfficeClock[] }> = ({ offices }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
      {offices.map((office, index) => {
        const t = localParts(now, office.timeZone);
        const open = WEEKDAYS.includes(t.weekday) && t.hour >= OPEN_HOUR && t.hour < CLOSE_HOUR;
        const time = `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;

        return (
          <motion.div
            key={office.city}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.05 }}
            className="flex flex-col items-center rounded-[24px] border border-slate-200/80 bg-white p-5 text-center shadow-[0_20px_50px_-40px_rgba(15,23,42,0.25)]"
          >
            <ClockFace hour={t.hour} minute={t.minute} second={t.second} open={open} />
            <p className="mt-4 text-base font-semibold text-slate-950">{office.city}</p>
            <p className="text-xs text-slate-500">{office.country}</p>
            <p className="mt-2 font-display text-xl font-bold tabular-nums text-slate-950">
              {time} <span className="text-xs font-medium text-slate-500">{t.weekday}</span>
            </p>
            <span
              className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${
                open ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${open ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {open ? 'Open now' : 'Closed'}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
};

export default OfficeClocks;
